import { CashFlow } from '@api/CashFlows/CashFlow';
import { cashText } from '@api/ActivityLogs/CashActivity';
import { describeChanges, FieldSpecs, quoted } from '@api/ActivityLogs/DescribeChanges';
import { formatPeriod } from '@utils/Expenses';
import { formatQuantity } from '@api/BranchOfficeInventories/BranchOfficeInventory';

// ============================================================================
// Los textos de la bitácora de GASTOS (plan de Gastos F3–F4). Sección `expense`,
// id = el del movimiento: la puerta es el botón "Actividad" de cada renglón de la
// pestaña Gastos. Crear no escribe (el movimiento ya guarda quién y cuándo, D6);
// editar, cancelar y confirmar sí.
//
// PURO, como `CashActivity`: el dinero con dos decimales y separador de miles.
// ============================================================================

export const EXPENSE_ACTIVITY_SECTION = 'expense';

export const EXPENSE_FIELDS: FieldSpecs<CashFlow> = {
    amount: { label: 'el monto', format: (value) => cashText(Number(value)) },
    categoryName: { label: 'la categoría', format: quoted },
    description: { label: 'la descripción', format: quoted },
    paidFromCash: {
        label: 'si salió de la caja',
        verbs: { on: 'lo marcó como pagado con el efectivo de la caja', off: 'lo marcó como pagado por fuera de la caja' }
    },
    period: { label: 'el mes', format: (value) => (value ? formatPeriod(String(value)) : 'ninguno') },
    payrollUserName: { label: 'a quién se le pagó', format: quoted },
    inputName: { label: 'el insumo', format: quoted },
    purchaseQuantity: { label: 'la cantidad comprada', format: (value) => (value === undefined || value === null ? 'ninguna' : String(value)) },
    purchaseUnitLabel: { label: 'la unidad de la compra', format: quoted }
};

/**
 * Lo que el server metió o sacó del inventario al igualar el ledger a este gasto
 * (plan de inventario F1): "entró al inventario: 138 rebanadas de Queso gouda" ·
 * "salió del inventario: …" (corrección o cancelación) · mezclado, "se ajustó".
 * `''` si no movió nada. Las cantidades son unidades base, como el ledger.
 */
export const describePurchaseSynced = (items: { inputName: string; quantity: number; unit: string }[]): string => {
    const moved = (items || []).filter((item) => Number(item.quantity));
    if (moved.length === 0) return '';
    const lista = (signed: boolean) =>
        moved
            .map((item) => `${signed ? (item.quantity > 0 ? '+' : '−') : ''}${formatQuantity(Math.abs(item.quantity))} ${item.unit} de ${item.inputName}`.trim())
            .join(', ');
    if (moved.every((item) => item.quantity > 0)) return `entró al inventario: ${lista(false)}`;
    if (moved.every((item) => item.quantity < 0)) return `salió del inventario: ${lista(false)}`;
    return `se ajustó en el inventario: ${lista(true)}`;
};

/** "editó el gasto: cambió el monto de $150.00 a $180.00". `''` = no cambió nada: no se escribe. */
export const describeEditedExpense = (before: CashFlow, after: CashFlow): string => {
    const changes = describeChanges(before, after, EXPENSE_FIELDS);
    return changes ? `editó el gasto: ${changes}` : '';
};

/** El monto y la descripción van dentro: es lo que alguien busca cuando el corte no cuadra. */
export const describeCanceledExpense = (flow: Pick<CashFlow, 'amount' | 'description'>, reason: string): string => {
    const descripcion = (flow.description || '').trim();
    const detalle = descripcion ? ` ("${descripcion}")` : '';
    return `canceló el gasto de ${cashText(flow.amount)}${detalle}. Motivo: ${reason.trim()}`;
};

/** Confirmar un fijo generado por el aseo: el monto del recibo y si salió de la caja. */
export const describeConfirmedExpense = (before: Pick<CashFlow, 'amount'>, amount: number, paidFromCash: boolean): string => {
    const monto =
        Math.round((Number(before.amount) || 0) * 100) === Math.round(amount * 100)
            ? `con el mismo monto (${cashText(amount)})`
            : `por ${cashText(amount)} (estimado: ${cashText(before.amount)})`;
    return `confirmó el gasto ${monto}${paidFromCash ? ', pagado con el efectivo de la caja' : ''}`;
};
